// Scanner preferences panel of the comparison screen.
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getScannerPrefs, upsertScannerPrefs, type PrefsOut } from '../../api/endpoints';
import { getErrorMessage } from '../../utils/errors';
import { AnimatedSwap } from './screenMotion';

const ALLERGENS = [
  'nickel','benzoic_acid','methylisothiazolinone','methylchloroisothiazolinone','parabens','lanolin','fragrance','linalool','limonene','citral','hydroxycitronellal','balsam_peru','tocopherol_allergy',
] as const;
type Allergen = typeof ALLERGENS[number];

const SENS_OPTIONS = [
  { v: 'none', label: 'None' },
  { v: 'possible', label: 'Possible' },
  { v: 'likely', label: 'Likely' },
] as const;

const ETHICAL_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'vegan', label: 'Vegan' },
  { v: 'halal', label: 'Halal' },
  { v: 'cruelty_free', label: 'Cruelty-free' },
] as const;

const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function Toggle({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="card card--compact card--bordered flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        {hint && <div className="t-small t-mute">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => { onChange(!checked); }}
        className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200"
        style={{ background: checked ? 'var(--c-kivo-teal)' : 'var(--c-charcoal-12)' }}
        aria-pressed={checked}
      >
        <span
          className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200"
          style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  );
}

export default function ComparePreferencesPage({ onBack }: { onBack: () => void }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [allergies, setAllergies] = useState<Allergen[]>([]);
  const [sensitivity, setSensitivity] = useState<'none' | 'possible' | 'likely'>('likely');
  const [acneProne, setAcneProne] = useState(false);
  const [ethical, setEthical] = useState<'off' | 'vegan' | 'halal' | 'cruelty_free'>('off');
  const [derm, setDerm] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const p: PrefsOut = await getScannerPrefs();
        setAllergies(p.allergies as Allergen[]);
        setSensitivity(p.sensitivity);
        setAcneProne(p.acne_prone);
        setEthical(p.ethical_mode);
        setDerm(p.cautious_actives);
      } catch (error: unknown) {
        setErr(getErrorMessage(error, 'Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAllergen = (a: Allergen) =>
    { setAllergies((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a])); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await upsertScannerPrefs({
        allergies,
        sensitivity,
        acne_prone: acneProne,
        ethical_mode: ethical,
        cautious_actives: derm,
      });
      onBack();
    } catch (error: unknown) {
      setErr(getErrorMessage(error, 'Failed to save'));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl py-4 sm:py-6">
      <div
        className="sticky top-0 z-10 px-4 sm:px-0 py-3"
        style={{ background: 'var(--c-cream-weak)', borderBottom: '1px solid var(--rule)', backdropFilter: 'blur(8px)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <h1 className="t-h3">Scanner preferences</h1>
          <div className="row-3">
            <button onClick={onBack} className="btn btn--ghost btn--sm">Skip</button>
            <button onClick={save} disabled={saving} className="btn btn--primary btn--sm">
              <AnimatedSwap value={saving ? 'Saving…' : 'Save'} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-0 pt-4">
        <div className="card card--sand card--compact">
          <span className="t-small" style={{ fontWeight: 500 }}>Optional.</span>
          <span className="t-small t-mute"> Personalize your preference scoring — or skip and set them any time.</span>
        </div>
      </div>

      <div className="px-4 sm:px-0 py-4 stack-5">
        {loading ? (
          <p className="t-small t-mute">Loading…</p>
        ) : (
          <>
            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ingredients to avoid</div>
              <p className="t-small t-mute">Select any known allergens to flag risky products.</p>
              <div className="flex flex-wrap gap-2">
                {ALLERGENS.map((a) => {
                  const active = allergies.includes(a);
                  return (
                    <button
                      key={a}
                      onClick={() => { toggleAllergen(a); }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={labelize(a)} />
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Skin sensitivity</div>
              <p className="t-small t-mute">How strict should we be about potential irritants?</p>
              <div
                className="grid grid-cols-3 w-full overflow-hidden"
                style={{ borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}
              >
                {SENS_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => { setSensitivity(o.v); }}
                    className="px-3 py-2 t-small text-center transition"
                    style={{
                      background: sensitivity === o.v ? 'var(--c-deep-teal)' : 'var(--c-cream)',
                      color: sensitivity === o.v ? 'var(--c-cream)' : 'var(--ink)',
                      transition: 'background var(--t-fast) var(--ease)',
                    }}
                  >
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ethical preference</div>
              <p className="t-small t-mute">Pick one.</p>
              <div className="flex flex-wrap gap-2">
                {ETHICAL_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => { setEthical(o.v); }}
                    className={ethical === o.v ? 'pill pill--teal' : 'pill'}
                    style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                  >
                    {ethical === o.v && <span className="dot" />}
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="grid sm:grid-cols-3 gap-3">
              <Toggle label="Breakout-prone" hint="Prefer non-comedogenic picks." checked={acneProne} onChange={setAcneProne} />
              <Toggle label="Prefer gentle formulas" hint="Go easy on strong actives." checked={derm} onChange={setDerm} />
            </section>

            <AnimatePresence>
              {err && (
                <motion.div
                  key={err}
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="t-small"
                  style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}
